import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, Info } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const WEEKDAYS_SHORT = ["L","M","M","J","V","S","D"]; // Mon..Sun

const TYPE_COLORS = {
  conges_payes: { bg: "#0066FF", label: "Congés payés" },
  rtt: { bg: "#7C3AED", label: "RTT" },
  maladie: { bg: "#059669", label: "Maladie" },
  annuel: { bg: "#0066FF", label: "Annuel" },
  sans_solde: { bg: "#94A3B8", label: "Sans solde" },
  exceptionnel: { bg: "#F59E0B", label: "Exceptionnel" },
};
const DEFAULT_COLOR = { bg: "#64748B", label: "Autre" };

function initials(name) {
  return (name || "?").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function Avatar({ userId, name, hasPhoto }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!hasPhoto) { setUrl(null); return; }
    let u; let cancelled = false;
    api.get(`/users/${userId}/photo`, { responseType: "blob" })
      .then((r) => { if (cancelled) return; u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => {});
    return () => { cancelled = true; if (u) URL.revokeObjectURL(u); };
  }, [userId, hasPhoto]);
  if (url) return <img src={url} alt={name} className="h-8 w-8 rounded-full object-cover" />;
  const colors = ["bg-[#0066FF]/15 text-[#0066FF]", "bg-violet-100 text-violet-700", "bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700", "bg-rose-100 text-rose-700"];
  const idx = (userId || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length;
  return <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${colors[idx]}`}>{initials(name)}</div>;
}

export default function TeamCalendar() {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const ym = `${cursor.y}-${String(cursor.m).padStart(2, "0")}`;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/team/calendar", { params: { year_month: ym } }); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ym]);

  const shift = (d) => setCursor((c) => {
    let m = c.m + d, y = c.y;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    return { y, m };
  });

  // Group leaves by user
  const grouped = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const it of data.items) {
      const arr = map.get(it.user_id) || { user_id: it.user_id, user_name: it.user_name, poste: it.poste, has_photo: it.has_photo, leaves: [] };
      arr.leaves.push(it);
      map.set(it.user_id, arr);
    }
    return Array.from(map.values());
  }, [data]);

  // Compute day cell status per user
  const daysInMonth = data?.days_in_month || 30;
  const dayHeaders = useMemo(() => {
    const arr = [];
    const start = new Date(cursor.y, cursor.m - 1, 1);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(cursor.y, cursor.m - 1, d);
      const dow = (dt.getDay() + 6) % 7; // 0=Mon..6=Sun
      arr.push({ d, dow, iso: `${cursor.y}-${String(cursor.m).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    return arr;
  }, [cursor, daysInMonth]);

  const isTodayHere = (d) =>
    cursor.y === today.getFullYear() && cursor.m === today.getMonth() + 1 && d === today.getDate();

  const cellFor = (userRow, d) => {
    const iso = `${cursor.y}-${String(cursor.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    for (const l of userRow.leaves) {
      if (iso >= l.date_debut && iso <= l.date_fin) return TYPE_COLORS[l.type_conge] || DEFAULT_COLOR;
    }
    return null;
  };

  const monthLabel = `${MONTHS[cursor.m - 1]} ${cursor.y}`;

  return (
    <div>
      <PageHeader
        title="Calendrier de l'équipe"
        subtitle="Visualisez qui est en congé ce mois-ci. Chaque couleur correspond à un type de congé."
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <Button data-testid="cal-prev" variant="outline" size="icon" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-heading text-lg font-semibold text-slate-900 min-w-[180px] text-center" data-testid="cal-month-label">{monthLabel}</div>
          <Button data-testid="cal-next" variant="outline" size="icon" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() + 1 })}>Aujourd'hui</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(TYPE_COLORS).filter(([k]) => ["conges_payes","rtt","maladie"].includes(k)).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-3 w-3 rounded" style={{ background: v.bg }}></span>{v.label}
            </span>
          ))}
        </div>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Chargement…</div>
        ) : grouped.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDays className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Aucun congé validé sur {monthLabel.toLowerCase()}</p>
            <p className="mt-1 text-xs text-slate-400 flex items-center justify-center gap-1"><Info className="h-3 w-3" /> Seuls les congés déjà approuvés par la RH apparaissent ici.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-slate-500 text-xs">
                  <th className="sticky left-0 z-10 bg-white text-left p-3 border-b border-slate-100 min-w-[220px]">Collègue</th>
                  {dayHeaders.map((h) => {
                    const isWeekend = h.dow >= 5;
                    return (
                      <th key={h.d} className={`p-1 border-b border-slate-100 text-center font-medium ${isWeekend ? "bg-slate-50" : ""}`}>
                        <div className={`w-6 mx-auto ${isTodayHere(h.d) ? "text-[#0066FF] font-bold" : ""}`}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400">{WEEKDAYS_SHORT[h.dow]}</div>
                          <div>{h.d}</div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grouped.map((row) => (
                  <tr key={row.user_id} data-testid={`cal-row-${row.user_id}`} className="hover:bg-slate-50/50">
                    <td className="sticky left-0 z-10 bg-white p-3 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <Avatar userId={row.user_id} name={row.user_name} hasPhoto={row.has_photo} />
                        <div>
                          <div className="font-medium text-slate-900 leading-tight">{row.user_name}</div>
                          <div className="text-xs text-slate-500">{row.poste || "—"}</div>
                        </div>
                      </div>
                    </td>
                    {dayHeaders.map((h) => {
                      const c = cellFor(row, h.d);
                      const isWeekend = h.dow >= 5;
                      return (
                        <td key={h.d} className={`p-1 border-b border-slate-100 ${isWeekend ? "bg-slate-50" : ""}`} title={c ? c.label : ""}>
                          {c ? (
                            <div className="h-5 w-6 mx-auto rounded" style={{ background: c.bg }}></div>
                          ) : (
                            <div className="h-5 w-6 mx-auto"></div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
